import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useRef } from 'react';

// third-party
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

// project imports
import Loader from 'ui-component/Loader';

import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';
import axios from 'utils/axiosClient';

// firebase initialize
if (!firebase.apps.length) {
    firebase.initializeApp({
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
    });
}

// const
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

// Optimistic boot: remember the last signed-in user so a page refresh renders the app
// immediately (with the correct role/routes) while Firebase + /me re-validate in the background.
const AUTH_CACHE_KEY = 'hdhd_auth_user';

const getInitialAuthState = () => {
    try {
        const cached = localStorage.getItem(AUTH_CACHE_KEY);
        if (cached) return { isLoggedIn: true, isInitialized: true, user: JSON.parse(cached) };
    } catch (e) {
        /* ignore corrupted cache */
    }
    return initialState;
};

const setCachedUser = (user) => {
    try {
        localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
    } catch (e) {
        /* ignore quota errors */
    }
};

const clearCachedUser = () => {
    try {
        localStorage.removeItem(AUTH_CACHE_KEY);
    } catch (e) {
        /* ignore */
    }
};

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

const FirebaseContext = createContext(null);

export const FirebaseProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState, getInitialAuthState);
    const fetchedForUid = useRef(null);

    useEffect(
        () =>
            firebase.auth().onAuthStateChanged((user) => {
                if (!user) {
                    fetchedForUid.current = null;
                    clearCachedUser();
                    dispatch({ type: LOGOUT });
                    return;
                }

                if (fetchedForUid.current === user.uid) return;
                fetchedForUid.current = user.uid;

                axios
                    .get('/me')
                    .then((response) => {
                        const loggedInUser = {
                            id: user.uid,
                            email: user.email,
                            name: user.displayName || 'None User',
                            maHs: response.maHs || '',
                            role: response?.isSupport
                                ? response?.maHs && response?.maHs !== 'BQL'
                                    ? 'HANHSINH'
                                    : response?.role
                                : response?.role || 'NONE',
                            hoTen: response?.hoTen || '',
                            phongLuuTru: response?.phongLuuTru || '',
                            donVi: response?.donVi || '',
                            nhomName: response?.nhomName || '',
                            vungName: response?.vungName || '',
                            sdt: response?.sdt || '',
                            isSupport: response?.isSupport || 'false'
                        };
                        setCachedUser(loggedInUser);
                        dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: loggedInUser } });
                    })
                    .catch((error) => {
                        console.log('error login', error);
                        fetchedForUid.current = null;
                        dispatch({
                            type: LOGIN,
                            payload: {
                                isLoggedIn: true,
                                user: {
                                    id: user.uid,
                                    email: user.email,
                                    name: user.displayName || 'None User',
                                    role: 'NONE'
                                }
                            }
                        });
                    });
            }),
        [dispatch]
    );

    const firebaseEmailPasswordSignIn = (email, password) => firebase.auth().signInWithEmailAndPassword(email, password);

    const firebaseGoogleSignIn = () => {
        const provider = new firebase.auth.GoogleAuthProvider();

        return firebase.auth().signInWithPopup(provider);
    };

    const firebaseRegister = async (email, password) => firebase.auth().createUserWithEmailAndPassword(email, password);

    const logout = () => firebase.auth().signOut();

    const resetPassword = async (email) => {
        await firebase.auth().sendPasswordResetEmail(email);
    };

    const updateProfile = () => {};
    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <FirebaseContext.Provider
            value={{
                ...state,
                firebaseRegister,
                firebaseEmailPasswordSignIn,
                login: () => {},
                firebaseGoogleSignIn,
                logout,
                resetPassword,
                updateProfile
            }}
        >
            {children}
        </FirebaseContext.Provider>
    );
};

FirebaseProvider.propTypes = {
    children: PropTypes.node
};

export default FirebaseContext;
